import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { AppBarTitleService, AppBarActionsService, AppBarAction, MessageService, FormHelper, AppPageService, SimpleDialogService, CommonActionKey } from 'common';
import { TokenService } from '../token.service';
import { TokenData } from '../token.model';
import { ClientData } from '../../../auth/client/client.data';
import { MatDialog } from '@angular/material/dialog';
import { UserPermissionService } from '../../../user/user-permission/user-permission.service';
import { readWrite } from '../../../user/user-permission/user-permission.data';
import { TokenUpdateDialogComponent } from '../token-update-dialog/token-update-dialog.component';

@UntilDestroy()
@Component({
    selector: 'ifb-token-details',
    templateUrl: './token-details.component.html',
    styleUrls: ['./token-details.component.scss'],
    standalone: false
})
export class TokenDetailsComponent implements OnInit {
  deleteSub: Subscription;
  form: UntypedFormGroup;
  data: TokenDetailsComponentData;
  minExpirationDate = new Date(new Date().setDate(new Date().getDate() + 1));
  readWriteToken: boolean;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly tokenService: TokenService,
    private readonly userPermissionService: UserPermissionService,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly messageService: MessageService,
    private readonly dialog: MatDialog,
    private readonly simpleDialogService: SimpleDialogService
  ) {

    this.appBarActionsService.actions = [
      { id: 'delete', label: 'Delete', disabled: true, buttonType: 'button' },
      { id: 'cancel', label: 'Cancel', buttonType: 'button' },
      { id: 'save', label: 'Save', buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' }
    ];

    this.initForm();
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.initData.bind(this));
    this.appBarActionsService.invoking.pipe(untilDestroyed(this)).subscribe(this.actionDispatch.bind(this));
    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(this.actionUpdate.bind(this));

    this.userPermissionService.granted([readWrite('admin-tokens')])
      .subscribe(res => {
        this.readWriteToken = res;
        this.appBarActionsService.enable('delete', res);
      });
  }

  actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  cancel(): void {
    this.appPageService.back();
  }

  copy(inputElement: any): void {
    inputElement.select();
    document.execCommand('copy');
    this.messageService.info("Token copied");
  }

  private initData(data: TokenDetailsComponentData): void {
    this.data = data;
    if (data.token)
      this.form.patchValue(data.token);

    this.appBarTitleService.title = data.token && data.token.id
      ? `Client ${data.token.client}`
      : 'Create new token';

    this.form.markAsPristine();
    this.actionUpdate();
  }

  delete(): void {
    if (!this.data || !this.data.token || !this.data.token.id)
      this.appPageService.back();

    this.simpleDialogService.openSimpleConfirmDialog(`Delete token?`, "Confirm")
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this.deleteSub = this.tokenService.delete(this.data.token.id).subscribe(() => {
            this.appPageService.back();
          });
        }
        this.actionUpdate();
      });
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);
    if (this.saveSub)
      return;

    if (!this.form.valid)
      return;

    if (!this.form.dirty) {
      this.cancel();
      return;
    }

    if (this.data?.token?.id) {
      TokenUpdateDialogComponent.show(this.dialog, this.form, "Update").subscribe(result => {
        if (result) {
          this.saveSub = this.tokenService
            .update(this.data.token.id, this.form.value)
            .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
          this.actionUpdate();
          this.appPageService.back();
        }
      });
    } else {
      const clientName = this.data.clients.find(_ => _.id === this.form.value.client).name;
      TokenUpdateDialogComponent.show(this.dialog, this.form, "Create", clientName).subscribe(result => {
        if (result)
          this.saveSub = this.tokenService
            .create(this.form.value)
            .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
        this.actionUpdate();
      });
    }
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      id: undefined,
      client: [undefined, Validators.required],
      description: undefined,
      expiresAt: new UntypedFormControl(undefined, { validators: Validators.required, updateOn: 'blur' }),
      active: [undefined, Validators.required],
      token: undefined,
      createdBy: undefined,
      createdOn: undefined
    });
  }

  private actionUpdate(): void {
    this.appBarActionsService
      .enable('delete', this.data && this.data.token && this.data.token.id && this.readWriteToken && !this.deleteSub ? true : false);
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
  }

  private saveSub: Subscription;

  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveSuccessHandler(token: TokenData): void {
    this.saveSubClear();
    if (this.data.token) {
      this.data.token = token;
      this.initData(this.data);
      this.actionUpdate();
    } else {
      this.router.navigate(['admin', 'token', token.id], { replaceUrl: true });
    }
  }
}

export interface TokenDetailsComponentData {
  token: TokenData;
  clients: ClientData[];
}
